/** Shared data-fetching and locale hooks. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { api, type Locale, type SiteConfig } from './api'
import { LOCALES, localeFromPath } from './i18n'

export function useLocale(): Locale {
  const { pathname } = useLocation()
  return localeFromPath(pathname)
}

interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

/** Fetch on mount and whenever `path` changes, discarding stale responses. */
export function useApi<T>(path: string | null): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(path !== null)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  // Guards against a slow earlier request resolving after a newer one and
  // overwriting the fresher data.
  const requestId = useRef(0)

  useEffect(() => {
    if (path === null) {
      setData(null)
      setLoading(false)
      return
    }
    const id = ++requestId.current
    setLoading(true)
    setError(null)

    api
      .get<T>(path)
      .then((result) => {
        if (id === requestId.current) {
          setData(result)
          setLoading(false)
        }
      })
      .catch((err: Error) => {
        if (id === requestId.current) {
          setError(err.message)
          setData(null)
          setLoading(false)
        }
      })
  }, [path, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, loading, error, reload }
}

let cachedConfig: SiteConfig | null = null

/** Site config is small and rarely changes, so it is fetched once per session. */
export function useSiteConfig(): SiteConfig | null {
  const [config, setConfig] = useState<SiteConfig | null>(cachedConfig)

  useEffect(() => {
    if (cachedConfig) return
    let cancelled = false
    api
      .get<SiteConfig>('/api/config')
      .then((result) => {
        cachedConfig = result
        if (!cancelled) setConfig(result)
      })
      .catch(() => {
        // The site must still render if config is unavailable; components fall
        // back to sensible defaults.
      })
    return () => {
      cancelled = true
    }
  }, [])

  return config
}

/** Set document title and meta description per page. */
export function useDocumentMeta(title: string, description?: string) {
  useEffect(() => {
    if (title) document.title = title
    if (description) {
      let tag = document.querySelector('meta[name="description"]')
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('name', 'description')
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', description)
    }
  }, [title, description])
}

/** Keep hreflang alternates in sync with the current route. Takes the same
 *  page's path in every locale (see pagePaths/productPaths in i18n.ts). */
export function useHreflang(paths: Record<Locale, string>) {
  // The record is rebuilt on every render; key the effect on its content.
  const signature = LOCALES.map((locale) => paths[locale]).join('|')
  useEffect(() => {
    const origin = window.location.origin
    const entries: [string, string][] = [
      ...LOCALES.map((locale): [string, string] => [locale, origin + paths[locale]]),
      ['x-default', origin + paths.de],
    ]

    const created: HTMLLinkElement[] = []
    for (const [lang, href] of entries) {
      let link = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${lang}"]`)
      if (!link) {
        link = document.createElement('link')
        link.rel = 'alternate'
        link.hreflang = lang
        document.head.appendChild(link)
        created.push(link)
      }
      link.href = href
    }

    return () => created.forEach((link) => link.remove())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])
}
